import {
  Box,
  Button,
  Modal,
  Radio,
  Slide,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  IconWrapper,
  InputField,
  IStyles,
  PrimaryButton,
  pxToRem,
  pxTovW,
  SelectMenu,
  theme,
} from '@geneo2-web/shared-ui';
import { ChangeEvent, ReactNode } from 'react';

const styles: IStyles = {
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100vw',
    marginY: { xs: pxToRem(10), md: pxTovW(25) },
    boxSizing: 'border-box',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    gap: { xs: pxToRem(40), md: pxTovW(20) },
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'center', md: 'flex-start' },
    width: { xs: '95%', md: 'unset' },
  },
  contentBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxTovW(25) },
    backgroundColor: 'white',
    width: { xs: '95%', md: pxTovW(1021) },
    borderRadius: { xs: pxToRem(10), md: pxTovW(20) },
    paddingX: { xs: pxToRem(10), md: pxTovW(35) },
    paddingY: { xs: pxToRem(10), md: pxTovW(30) },
    maxHeight: { xs: '85vh', md: '90vh' },
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  classSelectBox: {
    borderRadius: { xs: pxToRem(10), md: pxTovW(30) },
    border: '1px solid #D2D2D2',
    paddingX: { xs: pxToRem(10), md: pxTovW(30) },
    paddingY: { xs: pxToRem(10), md: pxTovW(15) },
  },
  classAndSectionContainer: {
    display: 'flex',
    gap: { xs: pxToRem(15), md: pxTovW(25) },
    width: '100%',
  },
  classSelectBoxHalf: {
    flex: 1,
    borderRadius: { xs: pxToRem(10), md: pxTovW(30) },
    border: '1px solid #D2D2D2',
    paddingX: { xs: pxToRem(10), md: pxTovW(30) },
    paddingY: { xs: pxToRem(10), md: pxTovW(15) },
  },
  lato27: {
    fontFamily: 'Lato',
    fontSize: { xs: pxToRem(15), md: pxTovW(27) },
  },
  radioContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: { xs: pxToRem(20), md: pxTovW(80) },
  },
  radioDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(7) },
  },
  radio: {
    color: '#CCE6FE',
    '& .MuiSvgIcon-root': {
      fontSize: { xs: pxToRem(22), md: pxTovW(34) },
    },
    '&.MuiRadio-root': {
      padding: '0',
    },
  },
  poppins24: {
    fontFamily: 'poppins',
    fontSize: { xs: pxToRem(12), md: pxTovW(24) },
  },
  addMoreBox: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: { xs: pxToRem(10), md: pxTovW(20) },
  },
  textInputField: {
    width: '100%',
    marginTop: { xs: pxToRem(10), md: pxTovW(18) },
    '& input': {
      width: '100%',
      height: { xs: pxToRem(40), md: pxTovW(60) },
      borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
      fontFamily: 'Lato',
      fontSize: { xs: pxToRem(14), md: pxTovW(28) },
    },
    '& .MuiOutlinedInput-root': {
      padding: '0',
      border: `${pxTovW(1)} solid #A8D3FC`,
      borderColor: '#A8D3FC',
      backgroundColor: '#DBF5FF',
      borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
      paddingX: { xs: pxToRem(10), md: pxTovW(20) },
    },
  },
  proceedButton: {
    width: { xs: '50%', md: pxTovW(500) },
    height: { xs: pxToRem(35), md: 'unset' },
    fontFamily: 'poppins',
    fontSize: { xs: pxToRem(14), md: pxTovW(31) },
    fontWeight: 'bold',
    paddingY: { xs: pxToRem(10), md: pxTovW(10) },
    margin: 'auto',
  },
};

interface IOptionList {
  name: string | number;
  id: number | bigint;
  nonEditableItem?: boolean;
  selectedNumber?: number;
  checked?: boolean;
}

interface IProps {
  popupOpen: boolean;
  popupCloseHandler: () => void;
  popupTitle: string;
  chooseClassData: {
    selectedClass: string;
    classList: IOptionList[];
    handleClassChange: (value: string) => void;
    disabled?: boolean;
  };
  chooseSectionData?: {
    selectedSection: string;
    sectionList: IOptionList[];
    handleSectionChange: (value: string) => void;
    disabled?: boolean;
  };
  radioData?: {
    title: string;
    value: string;
    disabled?: boolean;
    dataId?: string;
    controlRadioProps: (item: string) => {
      name: string;
      value: string;
      checked: boolean;
      onChange: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
    };
  }[];
  radioExtraNode?: ReactNode;
  nameChangeData: {
    title: string;
    name: string;
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  };
  sectionInputNode: ReactNode;
  addMoreSectionData: { handleAddMore: () => void; idAddMoreDisabled: boolean };
  proceedButtonData: { onClick: () => void; disabled: boolean };
}
export const SelectMultiSubjectsPopup = (props: IProps) => {
  const {
    popupTitle,
    popupOpen,
    popupCloseHandler,
    chooseClassData,
    chooseSectionData,
    radioData,
    radioExtraNode,

    nameChangeData,
    sectionInputNode,
    addMoreSectionData,
    proceedButtonData,
  } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Modal open={popupOpen} onClose={popupCloseHandler}>
      <Slide
        direction="up"
        in={popupOpen}
        mountOnEnter
        unmountOnExit
        timeout={500}
      >
        <Box sx={styles.root}>
          <Box sx={styles.container}>
            <Box sx={styles.contentBox}>
              <Typography
                data-id="teacher-homeworkmanage-faheader-text"
                variant={isMobile ? 'h3' : 'h1'}
                fontWeight="bold"
              >
                {popupTitle}
              </Typography>

              <Box sx={styles.classAndSectionContainer}>
                <Box sx={styles.classSelectBoxHalf}>
                  <Typography
                    data-id="teacher-homeworkmanage-fachooseclassheader-text"
                    variant="bodyText"
                    sx={styles.lato27}
                  >
                    Choose Class
                  </Typography>
                  <SelectMenu
                    dataId="teacher-homeworkmanage-fachooseclassdropdown-dropdown"
                    value={chooseClassData.selectedClass}
                    optionList={chooseClassData.classList}
                    onChange={chooseClassData.handleClassChange}
                    disabled={chooseClassData.disabled}
                    menuPaperProps={{
                      minWidth: '100px !important',
                      width: { xs: '90%', md: pxTovW(450) },
                      maxWidth: '100%',
                    }}
                    menuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                    rootStyles={{
                      width: '100%',
                      marginTop: { xs: pxToRem(10), md: pxTovW(18) },
                      height: { xs: pxToRem(40), md: pxTovW(60) },
                    }}
                  />
                </Box>

                {chooseSectionData && (
                  <Box sx={styles.classSelectBoxHalf}>
                    <Typography
                      data-id="teacher-homeworkmanage-fachoosesectionheader-text"
                      variant="bodyText"
                      sx={styles.lato27}
                    >
                      Choose Section
                    </Typography>
                    <SelectMenu
                      dataId="teacher-homeworkmanage-fachoosesectiondropdown-dropdown"
                      value={chooseSectionData.selectedSection}
                      optionList={chooseSectionData.sectionList}
                      onChange={chooseSectionData.handleSectionChange}
                      disabled={chooseSectionData.disabled}
                      menuPaperProps={{
                        minWidth: '100px !important',
                        width: { xs: '90%', md: pxTovW(450) },
                        maxWidth: '100%',
                      }}
                      menuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                      rootStyles={{
                        width: '100%',
                        marginTop: { xs: pxToRem(10), md: pxTovW(18) },
                        height: { xs: pxToRem(40), md: pxTovW(60) },
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box sx={styles.radioContainer}>
                {radioData?.map((item) => (
                  <Box key={item.value} sx={styles.radioDetails}>
                    <Radio
                      data-id={
                        item.dataId ||
                        (item.value === 'geneo'
                          ? 'teacher-homeworkmanage-fageneomarking-radiobtn'
                          : item.value === 'jee'
                          ? 'teacher-homeworkmanage-fajeemarking-radiobtn'
                          : item.value === 'custom'
                          ? 'teacher-homeworkmanage-facustommarking-radiobtn'
                          : undefined)
                      }
                      sx={styles.radio}
                      disabled={item.disabled}
                      {...item.controlRadioProps(item.value)}
                    />
                    <Typography variant="h3" sx={styles.poppins24}>
                      {item.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {radioExtraNode}

              <Box>
                {sectionInputNode}

                <Box sx={styles.addMoreBox}>
                  <Typography
                    data-id="teacher-homeworkmanage-faaddsubject-btn"
                    variant="linkText"
                    color="primary"
                    onClick={addMoreSectionData.handleAddMore}
                    sx={{
                      ...styles.poppins24,
                      cursor: addMoreSectionData.idAddMoreDisabled
                        ? 'not-allowed'
                        : 'pointer',
                      color: addMoreSectionData.idAddMoreDisabled
                        ? 'grey'
                        : 'primary',
                    }}
                  >
                    Add More+
                  </Typography>
                </Box>
              </Box>

              <Box sx={styles.classSelectBox}>
                <Typography
                  data-id="teacher-homeworkmanage-faassessmentname-text"
                  variant="bodyText"
                  sx={styles.lato27}
                >
                  {nameChangeData.title}
                </Typography>
                <InputField
                  dataId="teacher-homeworkmanage-faassessmentname-textinput"
                  variant="outlined"
                  fullWidth
                  type={'text'}
                  value={nameChangeData.value}
                  onChange={nameChangeData.onChange}
                  sx={styles.textInputField}
                />
              </Box>

              <PrimaryButton
                dataId="teacher-homeworkmanage-faproceedbtn-btn"
                variant="contained"
                onClick={proceedButtonData.onClick}
                sx={styles.proceedButton}
                disabled={proceedButtonData.disabled}
              >
                PROCEED
              </PrimaryButton>
            </Box>

            <Button
              data-id="teacher-homeworkmanage-faclosepopup-btn"
              onClick={popupCloseHandler}
              sx={{ p: '0px' }}
            >
              <IconWrapper
                name="close-yellow"
                size="md"
                parentFolder="icons"
                type="png"
                customSx={{
                  width: { xs: pxToRem(33), md: pxTovW(54) },
                  height: { xs: pxToRem(33), md: pxTovW(54) },
                }}
              />
            </Button>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};
